import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createClub, getClubEmbedPack, getClubMedia, listClubs, listClubTeams, me, patchClub, resetClubPassword,
  type ClubPortal, type ClubTeam,
} from '../api/portal';
import { ApiError } from '../api/client';
import { TeamLogoRequestsCard } from '../components/TeamBranding';
import { copyText } from '../lib/clipboard';

function generatePortalPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return `Dp-${out}`;
}

function ClubMediaBar() {
  const media = useQuery({ queryKey: ['portal-club-media'], queryFn: getClubMedia });
  const m = media.data;
  if (!m) return null;
  return (
    <div className="card mb-4 p-4 text-sm text-slate-300">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">Media sedišta</div>
      <div className="mt-1 text-white">
        {m.seats} aktivnih · liga naplaćuje klubovima {m.league_invoice_eur} € · Origo ligi {m.origo_invoice_eur} €
      </div>
      <div className="mt-1 text-xs text-slate-500">
        Predlog: {m.club_price_eur} € po klubu mesečno. Origo: {m.origo_fee_eur} € po aktivnom sedištu. Bez sedišta klub ne može da pošalje reel.
      </div>
    </div>
  );
}

export function ClubsPage() {
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const clubs = useQuery({
    queryKey: ['portal-clubs'],
    queryFn: listClubs,
    enabled: !!profile.data && !profile.data.parent_client_id,
  });
  const teams = useQuery({
    queryKey: ['portal-club-teams'],
    queryFn: listClubTeams,
    enabled: !!profile.data && !profile.data.parent_client_id,
  });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [teamQ, setTeamQ] = useState('');
  const [origins, setOrigins] = useState('');
  const [password, setPassword] = useState('');
  const [telegram, setTelegram] = useState('');
  const [bot, setBot] = useState('');
  const [reveal, setReveal] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createClub({
      company_name: name.trim(),
      contact_email: email.trim(),
      allowed_team_ids: teamIds,
      allowed_origins: origins.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
      portal_password: password.trim() || undefined,
      telegram_chat_id: telegram.trim() || undefined,
      telegram_bot_token: bot.trim() || undefined,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['portal-clubs'] });
      qc.invalidateQueries({ queryKey: ['portal-me'] });
      setReveal({ email: res.contact_email, password: res.portal_password });
      setCopied(false);
      setOpen(false);
      setName('');
      setEmail('');
      setTeamIds([]);
      setTeamQ('');
      setOrigins('');
      setPassword('');
      setTelegram('');
      setBot('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (profile.data?.parent_client_id) {
    return (
      <div>
        <div className="page-head">
          <div className="page-kicker">League package</div>
          <h1 className="page-title">Clubs</h1>
        </div>
        <div className="card p-4 text-sm text-slate-300">
          This login is a club under{' '}
          <span className="text-white">{profile.data.parent_company_name ?? 'the league'}</span>
          . Only the league portal can add clubs.
          {(profile.data.allowed_teams ?? []).length > 0 && (
            <> Your matches are limited to {(profile.data.allowed_teams ?? []).map((t) => t.name).join(', ')}.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">League package</div>
        <h1 className="page-title">Clubs</h1>
        <button type="button" className="btn-primary" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : '+ Add club'}
        </button>
        <p className="page-sub">
          Add a club, then run it: reset password, suspend, change the team, copy the site pack
          (live-score + fixtures + standings, already scoped to that team).
        </p>
      </div>

      <TeamLogoRequestsCard />

      {reveal && (
        <div className="card mb-4 space-y-2 p-4 ring-1 ring-amber-400/30">
          <div className="text-sm font-semibold text-white">Club portal password — copy it now</div>
          <p className="text-xs text-slate-400">
            Login at this portal with <code className="text-slate-200">{reveal.email}</code>
          </p>
          <code className="block rounded-lg bg-black/40 px-3 py-2 font-mono text-sm text-white">{reveal.password}</code>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => {
                void copyText(`${reveal.email}\n${reveal.password}`).then(() => setCopied(true));
              }}
            >
              {copied ? 'Copied' : 'Copy email + password'}
            </button>
            <button type="button" className="btn-ghost text-xs" onClick={() => setReveal(null)}>Dismiss</button>
          </div>
        </div>
      )}

      <div className="mb-4 text-[12px] text-slate-500">
        {clubs.data ? `${clubs.data.length} club portal${clubs.data.length === 1 ? '' : 's'}` : '—'}
      </div>

      {open && (
        <form
          className="form-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || !email.trim() || teamIds.length === 0) {
              setError('Name, email and at least one team are required.');
              return;
            }
            create.mutate();
          }}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="label">Club name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="RK Partizan" required />
            </div>
            <div>
              <label className="label">Portal email</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="media@club.rs" required />
            </div>
          </div>
          <TeamPicker
            teams={teams.data ?? []}
            selected={teamIds}
            takenBy={Object.fromEntries(
              (clubs.data ?? []).flatMap((c) =>
                [...(c.allowed_teams ?? []).map((t) => String(t.id)), ...(c.allowed_team_ids ?? [])]
                  .map((id) => [id, c.company_name] as const),
              ),
            )}
            query={teamQ}
            onQuery={setTeamQ}
            loading={teams.isLoading}
            onClear={() => setTeamIds([])}
            onToggle={(id, teamName) => {
              setTeamIds((prev) => {
                const has = prev.includes(id);
                const next = has ? prev.filter((x) => x !== id) : [...prev, id];
                if (!has && !name.trim()) setName(teamName);
                return next;
              });
            }}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="label">Widget hosts (optional)</label>
              <textarea className="textarea min-h-[64px]" value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="partizan.rs" />
            </div>
            <div>
              <label className="label">Portal password</label>
              <div className="flex gap-2">
                <input className="input font-mono" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to generate" />
                <button type="button" className="btn-secondary" onClick={() => setPassword(generatePortalPassword())}>Generate</button>
              </div>
              <label className="label mt-3">Telegram chat (optional)</label>
              <input className="input" value={telegram} onChange={(e) => setTelegram(e.target.value)} placeholder="telegram:-100…" />
              <input className="input mt-2" type="password" value={bot} onChange={(e) => setBot(e.target.value)} placeholder="Bot token" />
            </div>
          </div>
          {error && <div className="text-sm text-rose-300">{error}</div>}
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create club portal'}
          </button>
        </form>
      )}

      <ClubMediaBar />
      <div className="card overflow-hidden">
        {clubs.isLoading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
        {clubs.data && clubs.data.length === 0 && (
          <div className="p-6 text-sm text-slate-500">
            No club portals yet. Add the clubs that should log in and only see their matches.
          </div>
        )}
        {clubs.data && clubs.data.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Club</th>
                <th>Email</th>
                <th>Teams</th>
                <th>Portal</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clubs.data.map((c) => (
                <ClubRow
                  key={c.id}
                  club={c}
                  teams={teams.data ?? []}
                  allClubs={clubs.data ?? []}
                  onReveal={(email, password) => {
                    setReveal({ email, password });
                    setCopied(false);
                  }}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ClubRow({
  club, teams, allClubs, onReveal,
}: {
  club: ClubPortal;
  teams: ClubTeam[];
  allClubs: ClubPortal[];
  onReveal: (email: string, password: string) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [teamIds, setTeamIds] = useState(club.allowed_team_ids ?? []);
  const [teamQ, setTeamQ] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [pack, setPack] = useState<string | null>(null);

  const saveTeams = useMutation({
    mutationFn: () => patchClub(club.id, { allowed_team_ids: teamIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-clubs'] });
      setMsg('Team updated.');
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const resetPw = useMutation({
    mutationFn: () => resetClubPassword(club.id),
    onSuccess: (res) => {
      onReveal(club.contact_email, res.portal_password);
      qc.invalidateQueries({ queryKey: ['portal-clubs'] });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const suspend = useMutation({
    mutationFn: () => patchClub(club.id, { status: club.status === 'suspended' ? 'active' : 'suspended' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-clubs'] }),
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const mediaSeat = useMutation({
    mutationFn: () => patchClub(club.id, { club_media: !club.club_media }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-clubs'] });
      qc.invalidateQueries({ queryKey: ['portal-club-media'] });
      setMsg(club.club_media ? 'Media seat off.' : 'Media seat on. The club can share reels.');
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const copyPack = useMutation({
    mutationFn: () => getClubEmbedPack(club.id),
    onSuccess: (res) => {
      setPack(res.html);
      void copyText(res.html);
      setMsg(`Copied site pack for ${res.team_name} (${res.competition_code}).`);
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      <tr>
        <td className="font-medium text-white">{club.company_name}</td>
        <td className="text-slate-400">{club.contact_email}</td>
        <td className="text-xs text-slate-400">
          {(club.allowed_teams ?? []).map((t) => t.name).join(', ') || `${(club.allowed_team_ids ?? []).length} teams`}
        </td>
        <td>
          {club.status === 'suspended'
            ? <span className="badge-mute">suspended</span>
            : club.has_portal_password
            ? <span className="badge-good">login set</span>
            : <span className="badge-mute">no password</span>}
          {club.club_media && <span className="ml-2 text-xs text-amber-200">media</span>}
        </td>
        <td>
          <button type="button" className="btn-secondary text-xs" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : 'Manage'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-black/20 p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              <button type="button" className="btn-secondary text-xs" disabled={resetPw.isPending} onClick={() => resetPw.mutate()}>
                Reset password
              </button>
              <button type="button" className="btn-secondary text-xs" disabled={suspend.isPending} onClick={() => suspend.mutate()}>
                {club.status === 'suspended' ? 'Reactivate' : 'Suspend'}
              </button>
              <button type="button" className="btn-secondary text-xs" disabled={mediaSeat.isPending} onClick={() => mediaSeat.mutate()}>
                {club.club_media ? 'Turn off media seat' : 'Turn on media seat'}
              </button>
              <button type="button" className="btn-primary text-xs" disabled={copyPack.isPending} onClick={() => copyPack.mutate()}>
                Copy pack for club site
              </button>
            </div>
            <TeamPicker
              teams={teams}
              selected={teamIds}
              takenBy={Object.fromEntries(
                allClubs.flatMap((c) =>
                  [...(c.allowed_teams ?? []).map((t) => String(t.id)), ...(c.allowed_team_ids ?? [])]
                    .filter((id) => !(club.allowed_team_ids ?? []).includes(id))
                    .map((id) => [id, c.company_name] as const),
                ),
              )}
              query={teamQ}
              onQuery={setTeamQ}
              onToggle={(id) => setTeamIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
              onClear={() => setTeamIds([])}
            />
            <div className="mt-3 flex items-center gap-2">
              <button type="button" className="btn-primary text-xs" disabled={saveTeams.isPending || teamIds.length === 0} onClick={() => saveTeams.mutate()}>
                Save team
              </button>
              {msg && <span className="text-xs text-slate-400">{msg}</span>}
            </div>
            {pack && <pre className="mt-3 text-[11px] text-slate-400 whitespace-pre-wrap">{pack}</pre>}
          </td>
        </tr>
      )}
    </>
  );
}

function teamInitials(name: string): string {
  const stripped = name.replace(/^(GŽRK|ŽORK|ŽRK|RK|OK|KK|FK)\s+/i, '').trim();
  const parts = (stripped || name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function TeamPicker({
  teams,
  selected,
  takenBy = {},
  query,
  onQuery,
  onToggle,
  onClear,
  loading,
}: {
  teams: ClubTeam[];
  selected: string[];
  takenBy?: Record<string, string>;
  query: string;
  onQuery: (q: string) => void;
  onToggle: (id: string, name: string) => void;
  onClear?: () => void;
  loading?: boolean;
}) {
  const [scope, setScope] = useState<'all' | 'free' | 'taken'>('all');
  const q = query.trim().toLowerCase();
  const visible = teams.filter((t) => {
    const id = String(t.id);
    const taken = !!takenBy[id];
    if (scope === 'free' && taken) return false;
    if (scope === 'taken' && !taken) return false;
    if (!q) return true;
    return [t.name, t.short_name, t.sport_code, takenBy[id]].some((v) => (v ?? '').toLowerCase().includes(q));
  });
  const picked = teams.filter((t) => selected.includes(String(t.id)));
  const takenCount = teams.filter((t) => takenBy[String(t.id)]).length;

  return (
    <div className="rounded-xl bg-black/30 p-3 ring-1 ring-white/[0.08]">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="label mb-0">Teams in this portal</div>
          <div className="text-[12px] text-slate-500">
            {selected.length === 0 ? 'Pick the club’s team — usually one.' : `${selected.length} selected`}
          </div>
        </div>
        {selected.length > 0 && onClear && (
          <button type="button" className="btn-ghost text-xs" onClick={onClear}>Clear</button>
        )}
      </div>
      {picked.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {picked.map((t) => (
            <button
              key={t.id}
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[12px] text-emerald-100 ring-1 ring-emerald-400/30 hover:bg-emerald-400/25"
              onClick={() => onToggle(String(t.id), t.name)}
            >
              {t.name} <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          className="input min-w-[12rem] flex-1"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search teams…"
        />
        {([
          ['all', `All ${teams.length}`],
          ['free', `Free ${teams.length - takenCount}`],
          ['taken', `In use ${takenCount}`],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setScope(id)}
            className={scope === id ? 'btn-secondary text-xs' : 'btn-ghost text-xs'}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid max-h-[22rem] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
        {loading && <div className="col-span-full py-8 text-center text-xs text-slate-500">Loading teams…</div>}
        {!loading && visible.map((t) => {
          const id = String(t.id);
          const on = selected.includes(id);
          const owner = takenBy[id];
          return (
            <button
              key={id}
              type="button"
              onClick={() => onToggle(id, t.name)}
              className={`flex items-start gap-3 rounded-xl px-3 py-2.5 text-left ring-1 transition ${
                on
                  ? 'bg-emerald-400/10 ring-emerald-400/40'
                  : owner
                    ? 'bg-white/[0.02] ring-white/[0.06] hover:bg-white/[0.04]'
                    : 'bg-white/[0.03] ring-white/[0.08] hover:bg-white/[0.06]'
              }`}
            >
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[11px] font-semibold ${
                on ? 'bg-emerald-400 text-[#0b0d12]' : 'bg-white/10 text-white'
              }`}>
                {on ? '✓' : teamInitials(t.name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-white">{t.name}</span>
                <span className={`mt-0.5 block truncate text-[11px] ${
                  on ? 'text-emerald-200/80' : owner ? 'text-amber-200/70' : 'text-slate-500'
                }`}>
                  {on ? 'Selected' : owner ? `Already on ${owner}` : 'Available'}
                </span>
              </span>
            </button>
          );
        })}
        {!loading && visible.length === 0 && (
          <div className="col-span-full py-8 text-center text-xs text-slate-500">
            {teams.length === 0 ? 'No teams in this league’s competitions yet.' : 'Nothing matches that search.'}
          </div>
        )}
      </div>
    </div>
  );
}
